import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarDays, MapPin, Trophy, Plus, Trash2, Upload, Check,
  ChevronRight, ChevronLeft, Tv, Play, FileSpreadsheet, Loader2,
  Rocket, Zap, CheckCircle2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Create Event', icon: CalendarDays, description: 'Name, sport, location & dates' },
  { id: 2, title: 'Add Games', icon: Trophy, description: 'Manual entry or CSV import' },
  { id: 3, title: 'Configure Streams', icon: Tv, description: 'Optional stream setup' },
  { id: 4, title: 'Review & Launch', icon: Rocket, description: 'Final check & publish' },
];

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8" data-testid="step-indicator">
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isComplete = currentStep > step.id;
        return (
          <React.Fragment key={step.id}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
              isActive ? 'bg-rooam-red/20 border border-rooam-red/50' :
              isComplete ? 'bg-green-500/10 border border-green-500/30' :
              'bg-zinc-900 border border-zinc-800'
            }`}>
              <div className={`flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold ${
                isActive ? 'bg-rooam-red text-white' :
                isComplete ? 'bg-green-500 text-white' :
                'bg-zinc-800 text-zinc-500'
              }`}>
                {isComplete ? <Check className="h-3.5 w-3.5" /> : step.id}
              </div>
              <div className="hidden sm:block">
                <div className={`text-xs font-semibold ${isActive ? 'text-white' : isComplete ? 'text-green-400' : 'text-zinc-500'}`}>
                  {step.title}
                </div>
                <div className="text-[10px] text-zinc-600">{step.description}</div>
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className={`h-4 w-4 ${currentStep > step.id ? 'text-green-500' : 'text-zinc-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Step1CreateEvent({ eventForm, setEventForm }) {
  return (
    <div className="space-y-5" data-testid="step-1">
      <div>
        <Label className="text-zinc-300 text-sm">Event Name *</Label>
        <Input
          value={eventForm.name}
          onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
          placeholder="e.g., Summer Basketball Classic 2026"
          className="bg-zinc-900 border-zinc-700 text-white mt-1"
          data-testid="event-name-input"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Sport *</Label>
          <Select value={eventForm.sport} onValueChange={(v) => setEventForm({ ...eventForm, sport: v })}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white mt-1" data-testid="event-sport-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="basketball">Basketball</SelectItem>
              <SelectItem value="soccer">Soccer</SelectItem>
              <SelectItem value="volleyball">Volleyball</SelectItem>
              <SelectItem value="football">Football</SelectItem>
              <SelectItem value="multi_sport">Multi-Sport</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Location *</Label>
          <Input
            value={eventForm.location}
            onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
            placeholder="e.g., Charlotte Sports Arena"
            className="bg-zinc-900 border-zinc-700 text-white mt-1"
            data-testid="event-location-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Start Date *</Label>
          <Input
            type="date"
            value={eventForm.date}
            onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white mt-1"
            data-testid="event-date-input"
          />
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">End Date</Label>
          <Input
            type="date"
            value={eventForm.end_date}
            onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white mt-1"
          />
        </div>
      </div>
      <div>
        <Label className="text-zinc-300 text-sm">Description</Label>
        <Textarea
          value={eventForm.description}
          onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
          placeholder="Brief event description..."
          className="bg-zinc-900 border-zinc-700 text-white mt-1"
          rows={3}
        />
      </div>
    </div>
  );
}

function Step2AddGames({ games, setGames, eventForm, csvFile, setCsvFile, csvImporting, setCsvImporting, createdEventId, token }) {
  const addGame = () => {
    setGames([...games, {
      home_team_name: '',
      away_team_name: '',
      sport: eventForm.sport || 'basketball',
      location: '',
      scheduled_time: '',
      court: '',
    }]);
  };

  const updateGame = (index, field, value) => {
    const updated = [...games];
    updated[index] = { ...updated[index], [field]: value };
    setGames(updated);
  };

  const removeGame = (index) => {
    setGames(games.filter((_, i) => i !== index));
  };

  const handleCsvImport = async () => {
    if (!csvFile || !createdEventId) return;
    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await fetch(`${API_URL}/api/engine/games/bulk-import?event_id=${createdEventId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Imported ${data.imported} games`);
        // Mark existing manual games as imported
        if (data.games) {
          setGames(prev => [...prev, ...data.games.map(g => ({ ...g, _imported: true }))]);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'CSV import failed');
      }
    } catch {
      toast.error('CSV import failed');
    }
    setCsvImporting(false);
  };

  return (
    <div className="space-y-5" data-testid="step-2">
      {/* CSV Import Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
        <h4 className="text-zinc-300 text-sm font-medium flex items-center gap-2 mb-3">
          <FileSpreadsheet className="h-4 w-4 text-green-400" />
          Bulk Import (CSV)
        </h4>
        <div className="flex items-center gap-3">
          <Input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            className="bg-zinc-900 border-zinc-700 text-white text-sm file:bg-zinc-700 file:text-white file:border-0 flex-1"
            data-testid="csv-file-input"
          />
          <Button
            size="sm"
            onClick={handleCsvImport}
            disabled={!csvFile || csvImporting || !createdEventId}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="csv-import-btn"
          >
            {csvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Import
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-zinc-700 text-zinc-400"
            onClick={() => window.open(`${API_URL}/api/engine/games/template/csv`, '_blank')}
          >
            Template
          </Button>
        </div>
        {!createdEventId && (
          <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> Event must be created first (Step 1) before CSV import
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-zinc-800" />
        <span className="text-zinc-600 text-xs uppercase tracking-wider">or add manually</span>
        <div className="flex-1 h-px bg-zinc-800" />
      </div>

      {/* Manual Games */}
      <div className="space-y-3">
        {games.filter(g => !g._imported).map((game, index) => (
          <div key={index} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3" data-testid={`game-entry-${index}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-500 text-xs font-medium">Game {index + 1}</span>
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-6 w-6 p-0" onClick={() => removeGame(index)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={game.home_team_name}
                onChange={(e) => updateGame(index, 'home_team_name', e.target.value)}
                placeholder="Home Team *"
                className="bg-zinc-900 border-zinc-700 text-white text-sm"
                data-testid={`game-home-${index}`}
              />
              <Input
                value={game.away_team_name}
                onChange={(e) => updateGame(index, 'away_team_name', e.target.value)}
                placeholder="Away Team *"
                className="bg-zinc-900 border-zinc-700 text-white text-sm"
                data-testid={`game-away-${index}`}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input
                type="datetime-local"
                value={game.scheduled_time}
                onChange={(e) => updateGame(index, 'scheduled_time', e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-white text-xs"
                title="Scheduled Time"
              />
              <Input
                value={game.location}
                onChange={(e) => updateGame(index, 'location', e.target.value)}
                placeholder="Court / Location"
                className="bg-zinc-900 border-zinc-700 text-white text-sm"
              />
              <Select value={game.sport} onValueChange={(v) => updateGame(index, 'sport', v)}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="basketball">Basketball</SelectItem>
                  <SelectItem value="soccer">Soccer</SelectItem>
                  <SelectItem value="volleyball">Volleyball</SelectItem>
                  <SelectItem value="football">Football</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500" onClick={addGame} data-testid="add-game-btn">
        <Plus className="h-4 w-4 mr-2" /> Add Game
      </Button>
    </div>
  );
}

function Step3Streams({ games, streamConfigs, setStreamConfigs }) {
  const gamesWithNames = games.filter(g => g.home_team_name && g.away_team_name);

  const updateStream = (index, field, value) => {
    const updated = { ...streamConfigs };
    if (!updated[index]) updated[index] = {};
    updated[index][field] = value;
    setStreamConfigs(updated);
  };

  return (
    <div className="space-y-4" data-testid="step-3">
      <p className="text-zinc-500 text-sm">
        Configure stream URLs for each game. This step is optional — you can add streams later from the Admin Dashboard.
      </p>

      {gamesWithNames.length === 0 ? (
        <div className="text-center py-8">
          <Tv className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No games to configure streams for.</p>
          <p className="text-zinc-600 text-xs">Go back to Step 2 and add games first.</p>
        </div>
      ) : (
        gamesWithNames.map((game, index) => (
          <div key={index} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-sm font-medium">{game.home_team_name} vs {game.away_team_name}</span>
              <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">
                {streamConfigs[index]?.rtmp_url ? 'Configured' : 'No Stream'}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-500 text-[10px]">RTMP URL</Label>
                <Input
                  value={streamConfigs[index]?.rtmp_url || ''}
                  onChange={(e) => updateStream(index, 'rtmp_url', e.target.value)}
                  placeholder="rtmps://live.cloudflare.com:443/live/"
                  className="bg-zinc-900 border-zinc-700 text-white text-xs"
                />
              </div>
              <div>
                <Label className="text-zinc-500 text-[10px]">Stream Key</Label>
                <Input
                  value={streamConfigs[index]?.stream_key || ''}
                  onChange={(e) => updateStream(index, 'stream_key', e.target.value)}
                  placeholder="Your stream key"
                  className="bg-zinc-900 border-zinc-700 text-white text-xs"
                  type="password"
                />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Step4Review({ event, games, streamConfigs, createdEventId }) {
  const gamesWithNames = games.filter(g => g.home_team_name && g.away_team_name);
  const configuredStreams = Object.values(streamConfigs).filter(s => s?.rtmp_url).length;

  return (
    <div className="space-y-5" data-testid="step-4">
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-5 w-5 text-green-400" />
          <h3 className="text-green-400 font-semibold">Ready to Launch</h3>
        </div>
        <p className="text-zinc-400 text-sm">Review your event setup below. Click "Publish Event" to go live.</p>
      </div>

      {/* Event Summary */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-rooam-blue" /> Event Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Name</span><span className="text-white">{event.name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Sport</span><span className="text-white capitalize">{event.sport}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Location</span><span className="text-white">{event.location}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Date</span><span className="text-white">{event.date}{event.end_date ? ` — ${event.end_date}` : ''}</span></div>
        </CardContent>
      </Card>

      {/* Games Summary */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-400" /> Games ({gamesWithNames.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gamesWithNames.length === 0 ? (
            <p className="text-zinc-500 text-sm">No games added yet</p>
          ) : (
            <div className="space-y-1.5">
              {gamesWithNames.map((game, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-zinc-800/50 last:border-0">
                  <span className="text-white">{game.home_team_name} vs {game.away_team_name}</span>
                  <div className="flex items-center gap-2 text-zinc-500 text-xs">
                    {game.location && <span>{game.location}</span>}
                    <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">{game.sport}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Streams Summary */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Tv className="h-4 w-4 text-cyan-400" /> Streams ({configuredStreams} configured)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-zinc-500 text-sm">
            {configuredStreams > 0
              ? `${configuredStreams} game${configuredStreams > 1 ? 's' : ''} with stream configuration ready.`
              : 'No streams configured. You can add them later from the Admin Dashboard.'}
          </p>
        </CardContent>
      </Card>

      {createdEventId && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 text-center">
          <p className="text-zinc-500 text-xs">
            Game Day Mode URL: <code className="text-rooam-blue">/game-day/{createdEventId}</code>
          </p>
        </div>
      )}
    </div>
  );
}

export default function SetupWizard() {
  const navigate = useNavigate();
  const { token, canAccessAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [publishing, setPublishing] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [createdGameIds, setCreatedGameIds] = useState([]);

  // Step 1: Event
  const [eventForm, setEventForm] = useState({
    name: '', sport: 'basketball', location: '', date: '', end_date: '', description: '',
  });

  // Step 2: Games
  const [games, setGames] = useState([{
    home_team_name: '', away_team_name: '', sport: 'basketball', location: '', scheduled_time: '', court: '',
  }]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);

  // Step 3: Streams
  const [streamConfigs, setStreamConfigs] = useState({});

  const canProceed = () => {
    if (step === 1) return eventForm.name && eventForm.location && eventForm.date;
    if (step === 2) return true; // Games are optional, can add later
    if (step === 3) return true; // Streams are optional
    return true;
  };

  const handleNext = async () => {
    if (step === 1 && !createdEventId) {
      // Create the event
      try {
        const res = await fetch(`${API_URL}/api/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(eventForm)
        });
        if (res.ok) {
          const data = await res.json();
          setCreatedEventId(data.id);
          toast.success('Event created!');
          setStep(2);
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to create event');
        }
      } catch {
        toast.error('Failed to create event');
      }
      return;
    }

    if (step === 2) {
      // Create manual games
      const manualGames = games.filter(g => g.home_team_name && g.away_team_name && !g._imported && !g.game_id);
      const ids = [...createdGameIds];

      for (const game of manualGames) {
        try {
          const res = await fetch(`${API_URL}/api/engine/games`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              ...game,
              event_id: createdEventId,
              status: 'scheduled',
            })
          });
          if (res.ok) {
            const data = await res.json();
            ids.push(data.game_id);
          }
        } catch {
          // Continue with remaining games
        }
      }

      setCreatedGameIds(ids);
      if (manualGames.length > 0) toast.success(`${manualGames.length} game${manualGames.length > 1 ? 's' : ''} created`);
      setStep(3);
      return;
    }

    setStep(step + 1);
  };

  const handlePublish = async () => {
    setPublishing(true);

    // Configure streams for games that have stream configs
    const gamesWithNames = games.filter(g => g.home_team_name && g.away_team_name);
    for (let i = 0; i < gamesWithNames.length; i++) {
      const config = streamConfigs[i];
      if (config?.rtmp_url && createdGameIds[i]) {
        try {
          await fetch(`${API_URL}/api/engine/stream/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              game_id: createdGameIds[i],
              custom_rtmp_url: config.rtmp_url,
              custom_stream_key: config.stream_key || '',
            })
          });
        } catch {
          // Non-critical
        }
      }
    }

    setPublishing(false);
    toast.success('Event published successfully!');

    // Navigate to Game Day Mode
    if (createdEventId) {
      navigate(`/game-day/${createdEventId}`);
    } else {
      navigate('/admin');
    }
  };

  if (!canAccessAdmin()) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500">Access restricted to admins and operators.</p>
          <Button onClick={() => navigate('/events')} className="mt-4 bg-rooam-red">Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4" data-testid="setup-wizard">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Zap className="h-6 w-6 text-amber-400" />
            <h1 className="text-white text-2xl font-bold">Quick Start Wizard</h1>
          </div>
          <p className="text-zinc-500 text-sm">Set up your event in minutes</p>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={step} />

        {/* Step Content */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              {React.createElement(STEPS[step - 1].icon, { className: "h-5 w-5 text-rooam-red" })}
              {STEPS[step - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 1 && <Step1CreateEvent eventForm={eventForm} setEventForm={setEventForm} />}
            {step === 2 && (
              <Step2AddGames
                games={games}
                setGames={setGames}
                eventForm={eventForm}
                csvFile={csvFile}
                setCsvFile={setCsvFile}
                csvImporting={csvImporting}
                setCsvImporting={setCsvImporting}
                createdEventId={createdEventId}
                token={token}
              />
            )}
            {step === 3 && <Step3Streams games={games} streamConfigs={streamConfigs} setStreamConfigs={setStreamConfigs} />}
            {step === 4 && <Step4Review event={eventForm} games={games} streamConfigs={streamConfigs} createdEventId={createdEventId} />}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/admin')}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step < 4 ? (
            <Button
              className="bg-rooam-red hover:bg-red-600 text-white"
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="wizard-next-btn"
            >
              {step === 1 && !createdEventId ? 'Create Event' : 'Next'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handlePublish}
              disabled={publishing}
              data-testid="wizard-publish-btn"
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
              Publish Event
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
